/**
 * How far along the applicant's document pack is (COL-533).
 *
 * The applicant frame shows "1 of 2 uploaded" over an ID slot and an
 * income slot. V1 derives the same two counts inline in `Dashboard.tsx`
 * (`id_passport` and `proof_of_income` filters on `tenant_documents`); this is
 * the read-only half of that, so the applicant screen and the settings dialog
 * cannot disagree about whether something was uploaded.
 *
 * Returns the ROWS, not just the counts: the design lists each uploaded file
 * by name under its document (COL-533 design QA, Vivi's item 1), so a count
 * alone no longer renders the card.
 *
 * Still the only READER. Adding a file goes through the shared
 * `uploadTenantDocuments`, which V1's own mutation now calls too, so there is
 * still exactly one writer — the drift this hook's original note warned about
 * is avoided by sharing the writer rather than by refusing to write.
 *
 * Removing a file is deliberately absent: `tenant_documents` has no DELETE
 * policy for users, so a client delete matches zero rows and reports success
 * (the same trap `rename_tenant_document` was created for). That needs an
 * RPC, which is its own change.
 */

import { useQuery, useQueryClient } from "@tanstack/react-query";

import { useCallback, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import {
  IDENTITY_DOCUMENT_TYPE,
  INCOME_DOCUMENT_TYPE,
  uploadTenantDocuments,
  type TenantDocumentType,
} from "@/lib/tenant-document-upload";

/*
  The two kinds live with the type they inhabit, in `tenant-document-upload`.
  Re-exported here because this module is where callers already looked, and
  because importing them from a module a test mocks would make them undefined
  in that test.
*/
export { IDENTITY_DOCUMENT_TYPE, INCOME_DOCUMENT_TYPE };

/** One stored file, as the card lists it. */
export type TenantDocumentRow = {
  id: string;
  document_type: string;
  file_name: string | null;
  title: string | null;
  status: string | null;
  created_at: string | null;
};

export type TenantDocumentSummary = {
  identityCount: number;
  incomeCount: number;
  /** The files themselves, oldest first, so the list is stable as one is added. */
  identityFiles: TenantDocumentRow[];
  incomeFiles: TenantDocumentRow[];
  /** How many of the two kinds have at least one file. */
  uploadedKinds: number;
  totalKinds: number;
  isPending: boolean;
  isError: boolean;
};

export function useTenantDocumentSummary(): TenantDocumentSummary {
  const { user } = useAuth();

  const { data, isPending, isError } = useQuery<{
    identityFiles: TenantDocumentRow[];
    incomeFiles: TenantDocumentRow[];
  }>({
    // Under V1's `tenant-documents` prefix so its uploads invalidate this too.
    queryKey: ["tenant-documents", user?.id, "tenant-v2-summary"],
    enabled: !!user?.id,
    queryFn: async () => {
      if (!user?.id) return { identityFiles: [], incomeFiles: [] };
      const { data: rows, error } = await supabase
        .from("tenant_documents")
        .select("id,document_type,status,file_name,title,created_at")
        .eq("user_id", user.id)
        .in("document_type", [IDENTITY_DOCUMENT_TYPE, INCOME_DOCUMENT_TYPE]);
      if (error) throw error;
      const list = ((rows as any[]) ?? []).filter(
        // A rejected upload is not a document the applicant has supplied —
        // counting it would tell them the pack is complete while an admin is
        // waiting for a readable copy.
        (r) => String(r?.status ?? "").toLowerCase() !== "rejected"
      );
      // Oldest first: a newly added file joins the END of the list rather
      // than displacing the ones already on screen.
      const byAge = (a: TenantDocumentRow, b: TenantDocumentRow) =>
        String(a.created_at ?? "").localeCompare(String(b.created_at ?? ""));
      const of = (type: string) =>
        (list as TenantDocumentRow[])
          .filter((r) => r?.document_type === type)
          .sort(byAge);
      return {
        identityFiles: of(IDENTITY_DOCUMENT_TYPE),
        incomeFiles: of(INCOME_DOCUMENT_TYPE),
      };
    },
  });

  const identityFiles = data?.identityFiles ?? [];
  const incomeFiles = data?.incomeFiles ?? [];
  const identityCount = identityFiles.length;
  const incomeCount = incomeFiles.length;

  return {
    identityCount,
    incomeCount,
    identityFiles,
    incomeFiles,
    uploadedKinds: (identityCount > 0 ? 1 : 0) + (incomeCount > 0 ? 1 : 0),
    totalKinds: 2,
    isPending,
    isError,
  };
}

/**
 * Add files to one of the applicant's document slots.
 *
 * Wraps the SHARED `uploadTenantDocuments` — the same function V1's own
 * mutation calls — and refreshes both dashboards' queries afterwards, so a
 * file added on one screen is visible on the other without a reload.
 *
 * A hook rather than a bare call in the component so the auth dependency
 * stays out of the view, and so tests mock it exactly as they already mock
 * the read hook beside it.
 *
 * `uploadingType` names WHICH slot is busy, not merely that something is:
 * both inputs are disabled during an upload, and only the busy card should
 * say so.
 */
export function useUploadTenantDocuments() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [uploadingType, setUploadingType] = useState<TenantDocumentType | null>(
    null
  );

  const upload = useCallback(
    async (
      documentType: TenantDocumentType,
      files: File[],
      unknownErrorLabel?: string
    ): Promise<{ errors?: Array<{ fileName: string; error: string }> }> => {
      if (files.length === 0 || !user?.id) return {};
      setUploadingType(documentType);
      try {
        const result = await uploadTenantDocuments({
          files,
          documentType,
          userId: user.id,
          unknownErrorLabel,
        });
        // V1's prefixes too: its settings screen reads its own two queries.
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: ["tenant-documents"] }),
          queryClient.invalidateQueries({ queryKey: ["identity-documents"] }),
          queryClient.invalidateQueries({ queryKey: ["salary-documents"] }),
        ]);
        return result;
      } finally {
        setUploadingType(null);
      }
    },
    [user?.id, queryClient]
  );

  return { upload, uploadingType };
}
